/**
 * Collapsible "New customer" form. Self-gates on `customer.write`.
 *
 * Phone + national ID are validated server-side against the country
 * config (this form only checks they're non-empty); the service encrypts
 * them and stores lookup hashes. `photoRef` / `idDocRef` are object-store
 * references to media captured during onboarding — in the admin portal an
 * operator pastes the refs (the dealer mobile app populates them in the
 * real KYC flow, P1.B). `language` is a BCP-47-ish code; not hardcoded
 * here to keep the multi-country path open (ADR-0010).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CreateCustomerRequest, Customer } from "@dfp/api-client";
import { Button } from "./Button.js";
import { DocumentUploadField } from "./DocumentUploadField.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { GpsPinPicker, type GpsPin } from "./GpsPinPicker.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { languageLabel, useCountryConfig } from "../lib/useCountryConfig.js";

export function CreateCustomerForm(): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("customer.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const cfg = useCountryConfig(open);
  const [given, setGiven] = useState("");
  const [family, setFamily] = useState("");
  const [other, setOther] = useState("");
  const [phone, setPhone] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [photoRef, setPhotoRef] = useState("");
  const [idDocRef, setIdDocRef] = useState("");
  const [language, setLanguage] = useState("");
  const [vulnerableCustomer, setVulnerableCustomer] = useState(false);
  // P1.F follow-up — optional KYC address + GPS pin.
  const [houseNumber, setHouseNumber] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [gps, setGps] = useState<GpsPin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateCustomerRequest) => api.post<Customer>("/api/customers", body),
    onSuccess: () => {
      reset();
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["customers", "list"] });
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setGiven("");
    setFamily("");
    setOther("");
    setPhone("");
    setNationalId("");
    setPhotoRef("");
    setIdDocRef("");
    setLanguage("");
    setVulnerableCustomer(false);
    setHouseNumber("");
    setStreet("");
    setCity("");
    setRegion("");
    setGps(null);
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (given.trim().length < 1 || family.trim().length < 1) {
      setError("Given and family names are required.");
      return;
    }
    if (phone.trim().length < 1) {
      setError("Phone is required.");
      return;
    }
    if (nationalId.trim().length < 1) {
      setError("National ID is required.");
      return;
    }
    if (photoRef.trim().length < 1 || idDocRef.trim().length < 1) {
      setError("Photo reference and ID document reference are required.");
      return;
    }
    if (language.trim().length < 2) {
      setError("Language must be a 2–8 character code (e.g. sw, en).");
      return;
    }
    const otherTrimmed = other.trim();
    const address = buildAddress({ houseNumber, street, city, region });
    const gpsPayload =
      gps !== null ? { lat: gps.lat, lng: gps.lng, accuracyMetres: gps.accuracyM } : undefined;
    mutation.mutate({
      names: {
        given: given.trim(),
        family: family.trim(),
        ...(otherTrimmed.length > 0 ? { other: otherTrimmed } : {}),
      },
      phone: phone.trim(),
      nationalId: nationalId.trim(),
      photoRef: photoRef.trim(),
      idDocRef: idDocRef.trim(),
      language: language.trim(),
      vulnerableCustomer,
      ...(address ? { address } : {}),
      ...(gpsPayload ? { gps: gpsPayload } : {}),
    });
  }

  /** Build the address subset; returns null when every field is empty. */
  function buildAddress(parts: {
    houseNumber: string;
    street: string;
    city: string;
    region: string;
  }): CreateCustomerRequest["address"] {
    const out: Record<string, string> = {};
    if (parts.houseNumber.trim()) out["houseNumber"] = parts.houseNumber.trim();
    if (parts.street.trim()) out["street"] = parts.street.trim();
    if (parts.city.trim()) out["city"] = parts.city.trim();
    if (parts.region.trim()) out["region"] = parts.region.trim();
    return Object.keys(out).length === 0 ? undefined : (out as CreateCustomerRequest["address"]);
  }

  if (!canWrite) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>New customer…</Button>;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">New customer</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Given name">
          <TextInput value={given} onChange={(e) => setGiven(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Family name">
          <TextInput value={family} onChange={(e) => setFamily(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Other names" hint="optional">
          <TextInput value={other} onChange={(e) => setOther(e.target.value)} maxLength={200} />
        </Field>
        <Field label="Language">
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={!cfg.data}
          >
            <option value="">{cfg.data ? "Select language…" : "Loading…"}</option>
            {(cfg.data?.supportedLanguages ?? []).map((c) => (
              <option key={c} value={c}>
                {languageLabel(c)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Phone">
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="National ID">
          <TextInput value={nationalId} onChange={(e) => setNationalId(e.target.value)} />
        </Field>
        <DocumentUploadField
          label="Customer photo"
          kind="photo"
          value={photoRef}
          onChange={setPhotoRef}
        />
        <DocumentUploadField
          label="ID document"
          kind="id_doc"
          value={idDocRef}
          onChange={setIdDocRef}
        />
      </div>

      <details className="rounded-md border border-slate-200 bg-white p-3">
        <summary className="cursor-pointer text-xs font-medium text-slate-700">
          Address + GPS pin (optional — required for physical-address verification)
        </summary>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="House number">
              <TextInput value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} />
            </Field>
            <Field label="Street">
              <TextInput value={street} onChange={(e) => setStreet(e.target.value)} />
            </Field>
            <Field label="City">
              <TextInput value={city} onChange={(e) => setCity(e.target.value)} />
            </Field>
            <Field label="Region">
              <TextInput value={region} onChange={(e) => setRegion(e.target.value)} />
            </Field>
          </div>
          <GpsPinPicker value={gps} onChange={setGps} />
        </div>
      </details>

      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
        <input
          type="checkbox"
          checked={vulnerableCustomer}
          onChange={(e) => setVulnerableCustomer(e.target.checked)}
          className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        Vulnerable customer
      </label>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create customer"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
